/**
 * FileUpload component - Drag-and-drop file uploader for network configurations.
 * Supports OpenConfig YANG (XML) and Cisco IOS configuration files up to 50MB.
 * Cisco IOS files are automatically converted to OpenConfig YANG format.
 */

import { FC, useState, useRef, DragEvent, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { api } from '@/services/api';
import type { UploadResponse } from '@/types/entities';
import { useAuthStore } from '@/stores/authStore';

const MAX_FILE_SIZE = 52428800; // 50MB in bytes
const VALID_FILE_TYPES = [
  'text/xml',
  'application/xml',
  'text/plain',
];
// NOTE: RTF support removed - convert RTF files to .txt using textutil/unrtf first
const VALID_EXTENSIONS = ['.xml', '.cfg', '.txt', '.conf'];

interface FileUploadProps {
  onUploadSuccess?: () => void;
}

export const FileUpload: FC<FileUploadProps> = ({ onUploadSuccess }) => {
  const user = useAuthStore((state) => state.user);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validate file size and type.
   */
  const validateFile = (file: File): string | null => {
    // Check file size (50MB limit)
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 50MB limit (${(file.size / 1024 / 1024).toFixed(2)} MB)`;
    }

    // Check file extension (more reliable than MIME type for config files)
    const hasValidExtension = VALID_EXTENSIONS.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    // Allow file if it has valid extension OR valid MIME type
    // This is more lenient since RTF files may have different MIME types depending on OS
    const hasValidType = VALID_FILE_TYPES.includes(file.type);

    // Accept file if either extension matches OR it's a text/plain type (catch-all for text files)
    if (!hasValidExtension && !hasValidType && file.type !== '' && !file.type.startsWith('text/')) {
      return 'Invalid file type. Please upload a configuration file (.xml, .cfg, .txt, .rtf, .conf)';
    }

    return null; // Valid
  };

  /**
   * Handle file selection (from file input or drag-and-drop).
   */
  const handleFileSelect = (file: File) => {
    // Reset previous state
    setError(null);
    setUploadResult(null);
    setUploadProgress(0);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  /**
   * Handle file input change (Browse button).
   */
  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag over (prevent default to allow drop).
   */
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handle drag leave.
   */
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  /**
   * Handle file drop.
   */
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Upload the selected file.
   */
  const handleUpload = async () => {
    if (!selectedFile) return;
    if (!user) {
      setError('You must be logged in to upload files');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      const result = await api.upload(selectedFile, user.username, (progress) => {
        setUploadProgress(progress);
      });

      setUploadResult(result);
      setSelectedFile(null); // Clear for next upload

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call success callback if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to upload file'
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Open file browser.
   */
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Format file size for display.
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Upload Configuration File
        </Typography>

        {/* Drag-and-drop zone */}
        <Paper
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 4,
            mb: 2,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.400',
            backgroundColor: isDragging ? 'action.hover' : 'background.default',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={handleBrowseClick}
        >
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            Drag and drop your configuration file here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            or
          </Typography>
          <Button variant="contained" component="span">
            Browse Files
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
            Supported formats: OpenConfig YANG (XML) or Cisco IOS (.cfg, .txt, .rtf) • Maximum 50MB
          </Typography>
        </Paper>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.cfg,.txt,.rtf,.conf,text/*,application/xml,application/rtf"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        {/* Selected file info */}
        {selectedFile && !uploading && !uploadResult && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Selected:</strong> {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Typography>
            </Alert>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              fullWidth
            >
              Upload File
            </Button>
          </Box>
        )}

        {/* Upload progress */}
        {uploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {/* Upload result message */}
        {uploadResult && (
          <Alert
            severity={uploadResult.processingStatus === 'failed' ? 'error' : 'success'}
            icon={uploadResult.processingStatus === 'failed' ? <ErrorIcon /> : <SuccessIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" gutterBottom>
              <strong>
                {uploadResult.processingStatus === 'failed'
                  ? 'Upload completed with errors'
                  : 'Upload successful!'}
              </strong>
            </Typography>
            {uploadResult.processingMessage && (
              <Typography variant="body2" gutterBottom>
                {uploadResult.processingMessage}
              </Typography>
            )}
            <Typography variant="caption" display="block">
              Transaction ID: {uploadResult.transactionId}
            </Typography>
            <Typography variant="caption" display="block">
              Filename: {uploadResult.filename}
            </Typography>
            <Typography variant="caption" display="block">
              File Size: {formatFileSize(uploadResult.fileSize)}
            </Typography>
            {uploadResult.entitiesCreated && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Entities Created:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {uploadResult.entitiesCreated.switches !== undefined && (
                    <Typography variant="caption" component="span" sx={{ mr: 1 }}>
                      • {uploadResult.entitiesCreated.switches} switch{uploadResult.entitiesCreated.switches !== 1 ? 'es' : ''}
                    </Typography>
                  )}
                  {uploadResult.entitiesCreated.interfaces !== undefined && (
                    <Typography variant="caption" component="span" sx={{ mr: 1 }}>
                      • {uploadResult.entitiesCreated.interfaces} interface{uploadResult.entitiesCreated.interfaces !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {uploadResult.entitiesCreated.vlan_configs !== undefined && (
                    <Typography variant="caption" component="span" sx={{ mr: 1 }}>
                      • {uploadResult.entitiesCreated.vlan_configs} VLAN{uploadResult.entitiesCreated.vlan_configs !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {uploadResult.entitiesCreated.relationships !== undefined && (
                    <Typography variant="caption" component="span" sx={{ mr: 1 }}>
                      • {uploadResult.entitiesCreated.relationships} relationship{uploadResult.entitiesCreated.relationships !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {uploadResult.errorDetails && uploadResult.errorDetails.length > 0 && (
              <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'error.main' }}>
                {uploadResult.errorDetails.map((detail, index) => (
                  <Typography key={index} variant="caption" display="block" color="error.dark">
                    {detail}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
